import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import AIAssistant from './AIAssistant';
import { 
  LayoutDashboard, 
  Map, 
  AlertCircle, 
  Network, 
  Droplet, 
  Beaker, 
  FileText, 
  MessageSquare, 
  CheckSquare, 
  Video, 
  Settings,
  LogOut,
  Menu,
  X,
  User,
  ChevronRight,
  Gauge,
  IndianRupee,
  Wrench,
  Brain
} from 'lucide-react';

const Layout = ({ children, activeModule, setActiveModule }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const modules = [
    { id: 'dashboard', name: 'Executive Overview', icon: LayoutDashboard },
    { id: 'personnel', name: 'Key Personnel', icon: User },
    { id: 'map', name: 'Interactive Map', icon: Map },
    { id: 'ilkhunter', name: 'ILK Hunter', icon: AlertCircle },
    { id: 'valve-control', name: 'Valve Control', icon: Gauge },
    { id: 'billing', name: 'Billing & Revenue', icon: IndianRupee },
    { id: 'maintenance', name: 'Maintenance Planner', icon: Wrench },
    { id: 'predictive', name: 'Predictive Analytics', icon: Brain },
    { id: 'alerts', name: 'Action Center', icon: AlertCircle },
    { id: 'network', name: 'Network Analysis', icon: Network },
    { id: 'nrw', name: 'NRW Explorer', icon: Droplet },
    { id: 'quality', name: 'Water Quality', icon: Beaker },
    { id: 'reports', name: 'Reports', icon: FileText },
    { id: 'communication', name: 'Communication', icon: MessageSquare },
    { id: 'accountability', name: 'Accountability', icon: CheckSquare },
    { id: 'field', name: 'Field Verification', icon: Video },
    { id: 'settings', name: 'Customization', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27] flex relative overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Sidebar */}
      <div className={`bg-[#12172e] border-r border-cyan-500/20 text-white transition-all duration-300 relative z-10 ${
        sidebarOpen ? 'w-64' : 'w-20'
      } flex flex-col shadow-2xl`}>
        {/* Glow effect on border */}
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-cyan-500/50 to-transparent"></div>
        
        {/* Logo */}
        <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between relative">
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          
          {sidebarOpen ? (
            <div className="flex flex-col items-center w-full">
              {/* SWATI Logo replacing text */}
              <img 
                src="https://customer-assets.emergentagent.com/job_waterwise-24/artifacts/dpirsu80_logo_basic.png" 
                alt="SWATI Logo" 
                className="h-12 object-contain mb-1"
              />
              <p className="text-xs text-gray-400 tracking-wide">WATER MINISTERS WAR ROOM</p>
            </div>
          ) : (
            <div className="mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_waterwise-24/artifacts/dpirsu80_logo_basic.png" 
                alt="SWATI Logo" 
                className="w-10 h-10 object-contain"
              />
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded hover:bg-cyan-500/10 transition-colors border border-transparent hover:border-cyan-500/30"
          >
            {sidebarOpen ? <X size={18} className="text-cyan-400" /> : <Menu size={18} className="text-cyan-400" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 space-y-1 px-2">
          {modules.map((module) => {
            const Icon = module.icon;
            const isActive = activeModule === module.id;
            return (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all group relative overflow-hidden ${
                  isActive 
                    ? 'bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border border-cyan-500/50 text-cyan-400 glow-cyan' 
                    : 'hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/30'
                }`}
                data-testid={`nav-${module.id}`}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400 via-cyan-500 to-teal-500"></div>
                )}
                
                {/* Icon */}
                <div className={`p-1.5 rounded-md ${
                  isActive ? 'bg-cyan-500/20' : 'group-hover:bg-cyan-500/10'
                }`}>
                  <Icon size={18} className={isActive ? 'text-cyan-400' : 'text-gray-400 group-hover:text-cyan-400'} />
                </div>
                
                {/* Label */}
                {sidebarOpen && (
                  <span className={`text-sm font-medium tracking-wide ${
                    isActive ? 'text-cyan-400' : 'text-gray-300 group-hover:text-cyan-400'
                  }`}>
                    {module.name}
                  </span>
                )}
                
                {/* Chevron for active */}
                {sidebarOpen && isActive && (
                  <ChevronRight size={14} className="ml-auto text-cyan-400" />
                )}
              </button>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-cyan-500/20 relative">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shadow-lg glow-cyan">
                  {user?.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-white truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.role}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600/80 to-red-700/80 hover:from-red-600 hover:to-red-700 border border-red-500/50 rounded-lg transition-all hover:glow-red"
                data-testid="logout-button"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              className="p-2 rounded-lg w-full flex justify-center hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors"
            >
              <LogOut size={18} className="text-red-400" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        {/* Header */}
        <header className="bg-[#12172e] border-b border-cyan-500/20 px-6 py-4 relative">
          {/* Glow effect on border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white tracking-wide">
                {modules.find(m => m.id === activeModule)?.name}
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">Digital Command Center • Real-time Monitoring</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Status indicator */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-xs text-green-400 font-medium">SYSTEM ONLINE</span>
              </div>
              
              {/* User info */}
              <div className="flex items-center gap-3 p-2 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-white">{user?.name}</p>
                  <p className="text-xs text-gray-400">{user?.email}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold shadow-lg glow-cyan">
                  {user?.name?.charAt(0)}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6 bg-[#0a0e27]" data-testid="main-content">
          {children}
        </main>
      </div>

      {/* AI Assistant - Floating Chat Widget */}
      <AIAssistant />
    </div>
  );
};

export default Layout;